import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { COLLECTIONS } from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import type { BusinessUnit } from "./types";

export interface UseBusinessUnitResult {
  businessUnit: BusinessUnit | null;
  loading: boolean;
  error: Error | null;
}

export function useBusinessUnit(
  buId: string | null | undefined,
): UseBusinessUnitResult {
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset before re-subscribing — see the note in `useBuRequests`. A stale
    // error outlives the subscription that set it, so a later *successful*
    // snapshot repopulates the data while the consumer still renders the
    // failure. Keyed on a `buId` the caller may change without unmounting.
    setError(null);
    if (!buId) {
      setBusinessUnit(null);
      setLoading(false);
      return;
    }

    const ref = doc(db, COLLECTIONS.BUSINESS_UNITS, buId);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setBusinessUnit(null);
          setLoading(false);
          return;
        }
        setBusinessUnit({
          ...(snap.data() as Omit<BusinessUnit, "id">),
          id: snap.id,
        });
        setLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [buId]);

  return { businessUnit, loading, error };
}
